import { useState } from 'react';
import clsx from 'clsx';
import {
  Briefcase,
  Building2,
  CalendarDays,
  CheckCircle2,
  Database,
  Download,
  Folder,
  LayoutGrid,
  LogOut,
  Megaphone,
  PanelLeftClose,
  PanelLeftOpen,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  User,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthProvider';
import { useDiscoveryEnabled } from '@/features/event-discovery/useDiscoveryEnabled';
import { canUseOutreach } from '@/features/outreach/access';
import deusLogo from '@/assets/deuslogo.png';
import styles from './Sidebar.module.css';

interface NavItem {
  readonly name: string;
  readonly path: string;
  readonly icon: LucideIcon;
  /** Only rendered for admin and superadmin, mirroring require_admin server-side. */
  readonly adminOnly?: boolean;
  /** Only rendered for the roles that may open Outreach (superadmin, admin, manager). */
  readonly outreachOnly?: boolean;
  readonly discoveryOnly?: boolean;
}

/**
 * Navigation. Order and icons follow the prototype; `/users` and `/exports` are added
 * because the legacy app has them and the prototype never drew them.
 */
const NAV_ITEMS: readonly NavItem[] = [
  { name: 'Dashboard', path: '/', icon: LayoutGrid },
  { name: 'Import', path: '/import', icon: Download },
  { name: 'Projects', path: '/projects', icon: Folder },
  { name: 'People', path: '/people', icon: User },
  { name: 'Organisations', path: '/organisations', icon: Building2 },
  { name: 'Campaigns', path: '/campaigns', icon: Send },
  { name: 'Job Opportunities', path: '/job-opportunities', icon: Briefcase },
  { name: 'Outreach', path: '/outreach', icon: Megaphone, outreachOnly: true },
  { name: 'Event Discovery', path: '/event-discovery', icon: CalendarDays, discoveryOnly: true },
  { name: 'Master Data', path: '/master-data', icon: Database, adminOnly: true },
  { name: 'Settings', path: '/settings', icon: SlidersHorizontal, adminOnly: true },
  { name: 'Org Review', path: '/org-review', icon: CheckCircle2, adminOnly: true },
  { name: 'Users', path: '/users', icon: Users, adminOnly: true },
  { name: 'Compliance', path: '/compliance/approvals', icon: ShieldCheck, adminOnly: true },
];

export interface SidebarUser {
  readonly name: string;
  readonly initials: string;
  readonly role: string;
}

export interface SidebarProps {
  readonly user: SidebarUser;
  readonly isAdmin: boolean;
}

export function Sidebar({ user, isAdmin }: SidebarProps) {
  const { signOut, user: signedIn } = useAuth();

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('leadgen_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleSidebar = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('leadgen_sidebar_collapsed', String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const mayUseOutreach = signedIn !== null && canUseOutreach(signedIn.role);
  const discoveryEnabled = useDiscoveryEnabled();
  const items = NAV_ITEMS.filter(
    (item) =>
      (item.adminOnly !== true || isAdmin) &&
      (item.outreachOnly !== true || mayUseOutreach) &&
      (item.discoveryOnly !== true || discoveryEnabled),
  );

  return (
    <aside className={clsx(styles.sidebar, isCollapsed && styles.collapsed)}>
      {/* Brand & Collapse / Expand Toggle */}
      <div className={styles.brand}>
        {!isCollapsed ? (
          <>
            <NavLink to="/" className={clsx(styles.brandLink)} title="LeadGen Home">
              <div className={styles.brandIconBox}>
                <img src={deusLogo} alt="Deus" className={styles.brandIcon} />
              </div>
              <span className={styles.brandTitle}>
                Lead<span className={styles.brandTitleAccent}>Gen</span>
              </span>
            </NavLink>

            <button
              type="button"
              className={styles.toggleBtn}
              onClick={toggleSidebar}
              title="Collapse sidebar (icons only)"
              aria-label="Collapse sidebar"
            >
              <PanelLeftClose size={16} />
            </button>
          </>
        ) : (
          <div className={styles.collapsedBrandWrap}>
            <button
              type="button"
              className={styles.collapsedToggleBtn}
              onClick={toggleSidebar}
              title="Expand sidebar"
              aria-label="Expand sidebar"
            >
              <div className={styles.brandIconBox}>
                <img src={deusLogo} alt="Deus" className={styles.brandIcon} />
              </div>
              <span className={styles.collapsedExpandHint}>
                <PanelLeftOpen size={13} />
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Navigation List */}
      <nav className={styles.nav} aria-label="Main">
        <ul className={styles.menu}>
          {items.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  clsx(styles.link, isActive && styles.active, isCollapsed && styles.linkCollapsed)
                }
                title={isCollapsed ? item.name : undefined}
              >
                {({ isActive }) => (
                  <>
                    <item.icon className={styles.linkIcon} size={18} aria-hidden="true" />
                    {!isCollapsed && <span className={styles.linkText}>{item.name}</span>}
                    {isActive && <span className={styles.srOnly}>(current page)</span>}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer / User Profile */}
      <div className={clsx(styles.footer, isCollapsed && styles.footerCollapsed)}>
        {!isCollapsed ? (
          <div className={styles.userCard}>
            <NavLink
              to="/profile"
              className={({ isActive }) => clsx(styles.profile, isActive && styles.profileActive)}
              title="View profile"
            >
              <div className={styles.avatar}>
                <span className={styles.avatarInitials}>{user.initials}</span>
              </div>
              <div className={styles.userInfo}>
                <span className={styles.userName}>{user.name}</span>
                <span className={styles.userRole}>
                  <span className={styles.roleDot} aria-hidden="true" />
                  {user.role}
                </span>
              </div>
            </NavLink>

            <button
              type="button"
              className={styles.signOutBtn}
              onClick={() => signOut()}
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={15} aria-hidden="true" />
            </button>
          </div>
        ) : (
          <div className={styles.collapsedUserWrap}>
            <NavLink
              to="/profile"
              className={({ isActive }) =>
                clsx(styles.collapsedAvatarLink, isActive && styles.collapsedAvatarActive)
              }
              title={`${user.name} (${user.role})`}
            >
              <div className={styles.avatar}>
                <span className={styles.avatarInitials}>{user.initials}</span>
              </div>
            </NavLink>

            <button
              type="button"
              className={styles.collapsedSignOutBtn}
              onClick={() => signOut()}
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={14} aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
